import { Download, Edit3, Filter, X } from 'lucide-react';
import { DashboardTableSearchInput } from '@shigo-ui/dashboard-ui';
import { Button, SegmentedControl } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { AppRequestDatePreset, type AppRequestsListFilter } from '../../model';

const datePresetOptions = [
  { value: AppRequestDatePreset.Today, label: dashboardT('supplyApps.request.today') },
  { value: AppRequestDatePreset.Last7Days, label: dashboardT('supplyApps.request.last7Days') },
] as const;

interface AppRequestsTableToolbarProps {
  filter: AppRequestsListFilter;
  activeFilterCount: number;
  filtersOpen: boolean;
  selectedCount: number;
  canEdit: boolean;
  canExport: boolean;
  exportPending: boolean;
  onSearchChange: (value: string) => void;
  onDatePresetChange: (value: AppRequestDatePreset) => void;
  onFiltersOpenChange: (value: boolean) => void;
  onClearFilters: () => void;
  onEditSelected: () => void;
  onExport: () => void;
  publisherScoped?: boolean;
}

export function AppRequestsTableToolbar({
  filter,
  activeFilterCount,
  filtersOpen,
  selectedCount,
  canEdit,
  canExport,
  exportPending,
  onSearchChange,
  onDatePresetChange,
  onFiltersOpenChange,
  onClearFilters,
  onEditSelected,
  onExport,
  publisherScoped,
}: AppRequestsTableToolbarProps) {
  const search = useDebouncedValue(filter.searchTerm, onSearchChange, 650);

  return (
    <>
      <DashboardTableSearchInput
        aria-label={dashboardT('supplyApps.searchRequests')}
        placeholder={dashboardT('supplyApps.searchRequests')}
        value={search.draft}
        onChange={(event) => {
          search.setDraft(event.target.value);
        }}
      />
      {!publisherScoped ? (
        <SegmentedControl
          className="h-9"
          ariaLabel={dashboardT('supplyApps.request.datePreset')}
          value={filter.datePreset}
          options={datePresetOptions}
          onValueChange={onDatePresetChange}
        />
      ) : null}
      <div className="flex items-center">
        <Button
          type="button"
          variant="outline"
          className={activeFilterCount ? 'rounded-r-none' : undefined}
          aria-label={dashboardT('supplyApps.filters')}
          aria-expanded={filtersOpen}
          aria-controls="app-requests-filter-toolbar"
          onClick={() => {
            onFiltersOpenChange(!filtersOpen);
          }}
        >
          <Filter className="h-4 w-4" aria-hidden="true" />
          {dashboardT('supplyApps.filters')}
          {activeFilterCount ? (
            <span className="bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 text-[10px] font-bold">
              {activeFilterCount}
            </span>
          ) : null}
        </Button>
        {activeFilterCount ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive rounded-l-none border-l-0"
            aria-label={dashboardT('supplyApps.clearFilters')}
            onClick={onClearFilters}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        ) : null}
      </div>
      {!publisherScoped && selectedCount > 0 && canEdit ? (
        <Button type="button" onClick={onEditSelected}>
          <Edit3 className="h-4 w-4" />
          {dashboardT('supplyApps.request.editSelected')} ({selectedCount})
        </Button>
      ) : null}
      {!publisherScoped && canExport ? (
        <Button type="button" variant="outline" disabled={exportPending} onClick={onExport}>
          <Download className="h-4 w-4" />
          {dashboardT('supplyApps.request.export')}
        </Button>
      ) : null}
    </>
  );
}
